<template>
  <div class="card">

    <div class="bg-blue-light p-1">
      <TitleButton
        :showBtn="true"
        :showAddNew="false"
        :title="title"
        @onClickCloseButton="goToList"
      />
    </div>

    <div class="my-2 px-2">
      <TableHeaderHospitalBillAdd
        :formData="formData"
        :tableItems="tableItems"
        is-disabled
      />
    </div>

    <div class="col-12 px-2">
      <PartialInvoiceHospitalTable
        :tableItems="tableItems"
        :salesAccountHeads="salesAccountHeads"
        :vatRate="vatRate"
        :is-hide-add-btn="true"
        :from-return="true"
      />
    </div>
    <div class="row px-2">
      <div class="col-md-6">
        <div class="mb-1">
          <label class="form-label" for="description">Ledger Memo</label>
          <vField
            as="textarea"
            name="description"
            type="number"
            class="form-control"
            v-model="formData.description"
          />
        </div>
      </div>
      <div class="col-md-5 ml-auto mt-auto mb-1">
        <div class="d-flex flex-wrap gap-1 gy-2">
          <button @click="handleSubmit()" class="btn btn-primary">Update Return Bill</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import {inject, onMounted, provide, reactive, ref, watch} from 'vue';
import TitleButton from '@/components/atom/TitleButton';
import {useRoute, useRouter} from 'vue-router';
import handlePurchase from '@/services/modules/purchase';
import {vatRate} from '@/data/inventory.js';
import handleCompany from "@/services/modules/company";
import TableHeaderHospitalBillAdd from '@/components/molecule/company/hospital/billing/bill-manager/TableHeaderHospitalBillAdd.vue';
import handleHospitalBilling from "@/services/modules/hospital/billing";
import salesReturnHelper from "@/services/utils/pdf/salesReturnHelper";
import PartialInvoiceHospitalTable
  from "@/components/molecule/company/hospital/billing/bill-manager/edit/PartialInvoiceHospitalTable.vue";
import handlePurchaseAndSalesReturn from '@/services/modules/purchaseSalesReturn';

const {fetchProductList, fetchSalesBill, ...rest} = handlePurchase();
const {fetchCompanyInfo} = handleCompany();
const {editReturnBillings} = handleHospitalBilling();
const { fetchAndGeneratePdf } = salesReturnHelper();
const { fetchSalesReturn } = handlePurchaseAndSalesReturn();

const $route = useRoute();
const $router = useRouter();

const companyId = $route.params.companyId;
const loader = ref(true);
const salesAccountHeads = ref([]);
const totals = ref({});
const company = ref({});
const saleType = $route.query.sale_type ?? "investigation_invoice";
const title = ref('');

let formData = ref({
  company_id: companyId,
  contact_profile_id: null,
  account_head_id: null,
  ipd_register_id: null,
  vat_payable_account_head_id: null,
  business_id: null,
  project_id: null,
  cost_centre_id: null,
  location_id: null,
  status: 'active',
  mop_account_head_id: null,
  receipt_reference: null,
  discount_head_id: null,
  adjustment_amount: 0,
  receipt_description: "Invoice bill payment",
  payment: null,
  money_receipt_no: '',
  bill_number: '',
  date: '',
  has_item_detail: true,
  description: '',
  order_number: null,
  sales_person_id: null,
  commission_percent: null,
  lc_number: null,
  item_details: [],
  already_returned: 0
});

provide('formData', formData);
provide('totals', totals);
const showSuccess = inject('showSuccess');
const showError = inject('showError');

onMounted(async () => {
  const companyQuery = `?company_id=${companyId}`;
  const payableRes = rest.fetchAccountReceivable(companyQuery);
  const vatPayableRes = rest.fetchVatPayable(companyQuery);
  const fetchCompany = fetchCompanyInfo(companyId)
  const salesAccountHeadsRes = rest.getAccountHeadBySlag("sales_accounts", companyQuery)
  const getSalesReturn = fetchSalesReturn($route.params.saleReturnId, companyQuery)

  Promise.all([
    payableRes.then(res => {
      if (res.data) formData.value.account_head_id = res.data[0].id;
    }),
    vatPayableRes.then(res => {
      if (res.data) formData.value.vat_payable_account_head_id = res.data[0].id;
    }),
    fetchCompany.then(res => {
      if (res.data) company.value = res.data;
    }),
    salesAccountHeadsRes.then(res => {
      if (res.data) salesAccountHeads.value = res.data
    }),
    getSalesReturn.then(res => {
      if (res.data) {
        formData.value = res.data;
        formData.value.invoice_date = res.data.sale_master.date;
        formData.value.sale_subtotal_amount = res.data.sale_master.subtotal_amount;
        formData.value.sale_vat_amount = res.data.sale_master.vat_amount;
        formData.value.sale_total_amount = res.data.sale_master.total_amount;
        formData.value.sale_total_discount = res.data.sale_master.total_discount;
        formData.value.already_returned = res.data.total_amount;

        if(res.data.receipt_masters && res.data.receipt_masters[0]) {
          formData.value.receipt_master_id = res.data.receipt_masters[0].id;
          formData.value.mop_account_head_id = res.data.receipt_masters[0].account_head_id;
          formData.value.receipt_reference = res.data.receipt_masters[0].receipt_reference;
          formData.value.refund_amount = res.data.receipt_masters[0].refund_amount;
          formData.value.money_receipt_no = res.data.receipt_masters[0].voucher_no;
        }

        const items = res.data.general.map(td => {
          return {
            id: td.id,
            product_id: td.product_id,
            name: td.product.name,
            option_name: td.product.name,
            description: td.description,
            return_qty: td.quantity,
            prev_return_qty: td.prev_return_qty,
            rate: td.rate,
            discount: td.discount ?? 0,
            vat: td.vat,
            discount_amount: td.discount_amount ?? 0,
            discount_percent: td.discount_percent ?? 0,
            vat_amount: td.vat_amount,
            total: td.total,
            isEdit: false,
            product: {name: td.product.name},
            amount: td.amount,
            total_amount: td.total_amount,
            sale_general_quantity: td.sale_general_quantity,
            return_amount: td.return_amount,
            sale_general_id: td.sale_general_id,
            stock_register_id: td.stock_register_id,
            stock_in_hand: td.stock_in_hand,
          }
        });
        tableItems.push(...items);
        setInitialData();
      }
    })
  ])
    .then(() => {
      loader.value = false
    })
    .catch(() => {
      loader.value = false
    })
})

const setInitialData = () => {
  switch(saleType) {
    case 'investigation_invoice':
      title.value = 'Investigation Bill';
      formData.value.receipt_type = 'investigation_invoice_return';
      formData.value.receipt_description = 'Invoice bill payment';
      break;
    case 'pharmacy_invoice':
      title.value = 'Pharmacy Bill';
      formData.value.receipt_type = 'pharmacy_invoice_return';
      formData.value.receipt_description = 'Pharmacy bill payment';
      break;
    default:
      break;
  }
}

let tableItems = reactive([]);

const goToList = () => {
  const {redirect_route, ...routeQuery} = $route.query
  const routeName = redirect_route ? redirect_route : "bill-manager"
  $router.push({
    name: routeName,
    params: $route.params,
    query: {
        ...routeQuery
    }
  })
}

const handleSubmit = () => {
  loader.value = true

  formData.value.item_details = tableItems;
  let copyFormData = Object.assign({}, formData.value)
  let isAllItemsReturned = !formData.value.item_details.find(item => parseInt(item.return_qty ?? '0') !== (item.sale_general_quantity - item.prev_return_qty));
  copyFormData.is_return_edit = true;

  copyFormData.item_details = formData.value.item_details.map(item => {
    return {
      id: item.id,
      product_id: item.product_id,
      quantity: item.return_qty,
      rate: item.rate,
      vat: item.vat,
      discount_amount: item.discount_amount,
      discount_percent: item.discount_percent,
      vat_amount: item.vat_amount,
      amount: item.return_amount,
      total_amount: item.total_amount,
      sale_general_id: item.sale_general_id,
      stock_in_hand: item.stock_in_hand,
      prev_return_qty: item.prev_return_qty,
      description: item.description,
      stock_register_id: item.stock_register_id
    }
  })
  
  copyFormData.return_type = isAllItemsReturned ? "returned" : "partially_returned"
  copyFormData.adjustment_amount = formData.value.return_roundup_discount

  editReturnBillings($route.params.saleReturnId, copyFormData).then(res => {
    if (!res.status) {
      return showError(res.message)
    }
    showSuccess(res.message)
    fetchAndGeneratePdf(company.value, res.data.id, saleType)
    goToList();
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loader.value = false
  })
}

</script>
<style scoped>
address {
  margin-bottom: 0 !important;
}
</style>